import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  FaUsers, FaExchangeAlt, FaGift, FaStethoscope, FaAnchor,
  FaVideo, FaHospital, FaTrash, FaHandshake, FaPlus, FaChevronRight,
  FaSignOutAlt, FaUserShield, FaChevronDown, FaBell, FaFlask,
  FaHandHoldingUsd, FaFileUpload,
  // FaCreditCard, // used by the Credit Line Subscribers tile — see below
} from 'react-icons/fa';
import { GiPill } from 'react-icons/gi';

const MODULE_COLORS = {
  blue:   { bg: 'bg-blue-50',   icon: 'text-blue-600',   border: 'hover:border-blue-400',   ring: 'bg-blue-600'   },
  purple: { bg: 'bg-purple-50', icon: 'text-purple-600', border: 'hover:border-purple-400', ring: 'bg-purple-600' },
  rose:   { bg: 'bg-rose-50',   icon: 'text-rose-600',   border: 'hover:border-rose-400',   ring: 'bg-rose-600'   },
  amber:  { bg: 'bg-amber-50',  icon: 'text-amber-600',  border: 'hover:border-amber-400',  ring: 'bg-amber-600'  },
  teal:   { bg: 'bg-teal-50',   icon: 'text-teal-600',   border: 'hover:border-teal-400',   ring: 'bg-teal-600'   },
  green:  { bg: 'bg-green-50',  icon: 'text-green-600',  border: 'hover:border-green-400',  ring: 'bg-green-600'  },
  navy:   { bg: 'bg-indigo-50', icon: 'text-indigo-600', border: 'hover:border-indigo-400', ring: 'bg-indigo-600' },
  cyan:   { bg: 'bg-cyan-50',   icon: 'text-cyan-600',   border: 'hover:border-cyan-400',   ring: 'bg-cyan-600'   },
  violet: { bg: 'bg-violet-50', icon: 'text-violet-600', border: 'hover:border-violet-400', ring: 'bg-violet-600' },
  red:    { bg: 'bg-red-50',    icon: 'text-red-500',    border: 'hover:border-red-400',    ring: 'bg-red-500'    },
  orange: { bg: 'bg-orange-50', icon: 'text-orange-600', border: 'hover:border-orange-400', ring: 'bg-orange-600' },
  emerald:{ bg: 'bg-emerald-50',icon: 'text-emerald-600',border: 'hover:border-emerald-400',ring: 'bg-emerald-600'},
};

const ModuleCard = ({ icon: Icon, title, description, color = 'blue', onClick }) => {
  const c = MODULE_COLORS[color];
  return (
    <div
      onClick={onClick}
      className={`group relative bg-white rounded-xl border border-gray-200 p-5 cursor-pointer
        transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${c.border}`}
    >
      {/* Top row: icon + arrow */}
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 rounded-xl ${c.bg} ${c.icon} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <FaChevronRight className="w-3 h-3 text-gray-300 group-hover:text-gray-500 transition-colors mt-1" />
      </div>

      {/* Title */}
      <p className="text-sm font-semibold text-gray-900 leading-snug mb-1">{title}</p>

      {/* Description */}
      {description && (
        <p className="text-xs text-gray-400 leading-relaxed">{description}</p>
      )}

      {/* Bottom accent bar */}
      <div className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-b-xl ${c.ring} opacity-0 group-hover:opacity-100 transition-opacity`} />
    </div>
  );
};

const SubscriberPortalHome = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef(null);

  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance',
      'wallet_ops': 'Ops',
      'wallet_support': 'Support',
    };
    
    return roleMapping[authRole] || null;
  })();

  const canAccessRedemptionRequests    = currentRole === 'Finance' || currentRole === 'SuperAdmin';
  const canAccessManualTransaction     = currentRole === 'Ops' || currentRole === 'Finance' || currentRole === 'SuperAdmin';
  const canAccessHospitalPayment       = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const canAccessDiagnosticsAndMedicine= currentRole === 'Support' || currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const canAccessMedicine              = currentRole === 'Support' || currentRole === 'Finance' || currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const canAccessAnchorWallets         = currentRole === 'Finance' || currentRole === 'SuperAdmin';
  const canAccessDoctor                = currentRole === 'Finance' || currentRole === 'SuperAdmin' || currentRole === 'Ops';
  const canAccessVirtualConsultation   = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const canAccessAddMedicine           = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const canAccessAccountDeletion       = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const canAccessPartnerEnquiries      = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const canAccessCEP                   = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const canAccessHospitalCMS           = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const canAccessDiagnosticLabs        = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const canAccessLoanSubscribers       = currentRole === 'Ops' || currentRole === 'SuperAdmin' || currentRole === 'Support';
  // const canAccessCreditLine            = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const canAccessAdminUsers            = currentRole === 'SuperAdmin';
  const canAccessBulkIssue             = currentRole === 'SuperAdmin';


  const ROLE_STYLE = {
    SuperAdmin: { badge: 'bg-purple-100 text-purple-700', avatar: 'bg-purple-600' },
    Finance:    { badge: 'bg-blue-100 text-blue-700',     avatar: 'bg-blue-600'   },
    Ops:        { badge: 'bg-green-100 text-green-700',   avatar: 'bg-green-600'  },
    Support:    { badge: 'bg-amber-100 text-amber-700',   avatar: 'bg-amber-500'  },
  };
  const roleStyle = ROLE_STYLE[currentRole] || { badge: 'bg-gray-100 text-gray-600', avatar: 'bg-gray-500' };
  const roleInitial = currentRole?.[0] ?? '?';

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Subscriber Wallet.</p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar — centered card, not full-width */}
      <div className="px-6 pt-5">
        <div className="max-w-6xl mx-auto bg-white border border-gray-200 rounded-2xl shadow-sm px-6 py-3.5 flex items-center justify-between">
          {/* Left: brand */}
          <div>
            <h1 className="text-base font-bold text-gray-900 leading-tight">Subscriber Management Portal</h1>
            <p className="text-xs text-gray-400 mt-0.5">Admin dashboard · operations</p>
          </div>

          {/* Right: avatar with dropdown */}
          <div className="flex items-center gap-2">
            <div className="relative" ref={avatarRef}>
            <button
              onClick={() => setAvatarOpen(v => !v)}
              className="flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all bg-white"
            >
              {/* Avatar circle */}
              <div className={`w-8 h-8 rounded-full ${roleStyle.avatar} text-white text-sm font-bold flex items-center justify-center select-none`}>
                {roleInitial}
              </div>
              <FaChevronDown className={`w-2.5 h-2.5 text-gray-400 transition-transform duration-200 ${avatarOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            {avatarOpen && (
              <div className="absolute right-0 mt-2.5 w-52 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden">
                {/* User info */}
                <div className="px-4 py-3.5 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${roleStyle.avatar} text-white text-sm font-bold flex items-center justify-center shrink-0`}>
                      {roleInitial}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 leading-none mb-1">Signed in as</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${roleStyle.badge}`}>
                        <FaUserShield className="w-2.5 h-2.5" />
                        {currentRole}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Logout */}
                <div className="p-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors font-medium"
                  >
                    <FaSignOutAlt className="w-3.5 h-3.5" />
                    Logout
                  </button>
                </div>
              </div>
            )}
            </div>{/* end relative */}
          </div>{/* end flex gap-2 */}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Modules</h2>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">

          {/* ── Subscribers ── */}
          <ModuleCard
            icon={FaUsers}
            title="Subscribers"
            description="View & manage subscriber profiles"
            color="blue"
            onClick={() => navigate('/subscriber-wallet/subscribers')}
          />

          {/* ── Hospital ── */}
          {canAccessHospitalPayment && (
            <ModuleCard
              icon={FaHospital}
              title="Hospital Payment"
              description="Hospital billing & cashback"
              color="rose"
              onClick={() => navigate('/subscriber-wallet/hospital-payment')}
            />
          )}

          {canAccessHospitalCMS && (
            <ModuleCard
              icon={FaHospital}
              title="Hospital CMS"
              description="Manage hospital listings"
              color="cyan"
              onClick={() => navigate('/subscriber-wallet/hospitals')}
            />
          )}

          {canAccessDiagnosticLabs && (
            <ModuleCard
              icon={FaFlask}
              title="Diagnostic Labs"
              description="Labs & chains onboarding"
              color="teal"
              onClick={() => navigate('/subscriber-wallet/labs/labs')}
            />
          )}

          {/* ── Health Services ── */}
          {canAccessDiagnosticsAndMedicine && (
            <ModuleCard
              icon={FaStethoscope}
              title="Diagnostics"
              description="Lab & diagnostic bookings"
              color="teal"
              onClick={() => navigate('/subscriber-wallet/diagnostics')}
            />
          )}

          {canAccessDoctor && (
            <ModuleCard
              icon={FaStethoscope}
              title="Doctors"
              description="Manage doctor profiles & slots"
              color="cyan"
              onClick={() => navigate('/subscriber-wallet/doctors')}
            />
          )}

          {canAccessVirtualConsultation && (
            <ModuleCard
              icon={FaVideo}
              title="Virtual Consultation"
              description="Online consult sessions"
              color="violet"
              onClick={() => navigate('/subscriber-wallet/virtual-consultation')}
            />
          )}

          {canAccessMedicine && (
            <ModuleCard
              icon={GiPill}
              title="Medicine"
              description="Medicine orders & tracking"
              color="green"
              onClick={() => navigate('/subscriber-wallet/medicine')}
            />
          )}

          {canAccessAddMedicine && (
            <ModuleCard
              icon={FaPlus}
              title="Add Medicine"
              description="Add new medicine to catalog"
              color="emerald"
              onClick={() => navigate('/subscriber-wallet/add-medicine')}
            />
          )}

          {/* ── Finance ── */}
          {canAccessManualTransaction && (
            <ModuleCard
              icon={FaExchangeAlt}
              title="Manual Transaction"
              description="Process wallet transactions"
              color="purple"
              onClick={() => navigate('/subscriber-wallet/manual-transaction')}
            />
          )}

          {canAccessRedemptionRequests && (
            <ModuleCard
              icon={FaGift}
              title="Redemption Requests"
              description="Review redemption claims"
              color="amber"
              onClick={() => navigate('/subscriber-wallet/redemption-requests')}
            />
          )}

          {canAccessAnchorWallets && (
            <ModuleCard
              icon={FaAnchor}
              title="Anchor Wallets"
              description="Partner anchor wallet overview"
              color="navy"
              onClick={() => navigate('/financedashboard')}
            />
          )}

          {/* ── Partner & Marketing ── */}
          {canAccessPartnerEnquiries && (
            <ModuleCard
              icon={FaHandshake}
              title="Ayushpay Leads"
              description="Partner enquiry submissions"
              color="orange"
              onClick={() => navigate('/subscriber-wallet/partner-enquiries')}
            />
          )}

          {/* Engagement Logs and Application Events are reached via tabs
              inside Customer Engagement now, not separate Home tiles. */}
          {canAccessCEP && (
            <ModuleCard
              icon={FaBell}
              title="Customer Engagement"
              description="Manage engagement campaigns, logs, and events"
              color="violet"
              onClick={() => navigate('/subscriber-wallet/engagement')}
            />
          )}

          {canAccessLoanSubscribers && (
            <ModuleCard
              icon={FaHandHoldingUsd}
              title="Loan Subscribers"
              description="View loan journey subscribers"
              color="emerald"
              onClick={() => navigate('/subscriber-wallet/loan-subscribers')}
            />
          )}


          {/* Credit Line Subscribers tile temporarily hidden from the dashboard.
              Pages/routes remain intact and reachable via direct URL.
          {canAccessCreditLine && (
            <ModuleCard
              icon={FaCreditCard}
              title="Credit Line Subscribers"
              description="View credit line accounts and lender status"
              color="navy"
              onClick={() => navigate('/subscriber-wallet/credit-line-subscribers')}
            />
          )}
          */}

          {/* ── Admin ── */}
          {canAccessAdminUsers && (
            <ModuleCard
              icon={FaUserShield}
              title="Admin Users"
              description="Create & manage admin accounts"
              color="purple"
              onClick={() => navigate('/subscriber-wallet/admin-users')}
            />
          )}

          {canAccessBulkIssue && (
            <ModuleCard
              icon={FaFileUpload}
              title="Bulk Issue Subscriptions"
              description="Upload an anchor's subscribers from Excel"
              color="purple"
              onClick={() => navigate('/subscriber-wallet/bulk-issue-subscriptions')}
            />
          )}

          {canAccessAccountDeletion && (
            <ModuleCard
              icon={FaTrash}
              title="Account Deletion"
              description="Review deletion requests"
              color="red"
              onClick={() => navigate('/subscriber-wallet/account-deletion-requests')}
            />
          )}

        </div>
      </div>
    </div>
  );
};

export default SubscriberPortalHome;
